import { styled } from "goober";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WORKFLOW_RUNS_LIMIT } from "../../shared/queries";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { smoothPath, type Point } from "../lib/smoothPath";
import {
  ChartMarker,
  ChartPill,
  ChartTooltip,
  ChartTooltipCount,
  ChartTooltipDot,
  ChartTooltipHeader,
  ChartTooltipRow,
  useClampRight,
} from "./chartHover";
import { Icon } from "./Icon";
import { Text } from "./Text";

const PADDING_TOP = 32;
const PADDING_BOTTOM = 4;
const PADDING_X = 24;
const VIEW_WIDTH = 960;
const VIEW_HEIGHT = 280;
const GRAPH_WIDTH = VIEW_WIDTH - PADDING_X * 2;
const GRAPH_HEIGHT = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
const BASELINE_Y = PADDING_TOP + GRAPH_HEIGHT;

const DEFAULT_WINDOW_MS = 3 * 60 * 60 * 1000; // 3 hours
const BUCKET_COUNT = 36;
const NOW_BUCKET = 27; // "now" sits 3/4 across the graph
const TOTAL_DISPLAY_BUCKETS = 36; // includes future space to the right

const LERP_SPEED = 0.12;

const Wrapper = styled("div")`
  position: absolute;
  left: 32px;
  right: 32px;
  bottom: calc(34px + 20px + 22px);
  top: 0;
  display: flex;
  flex-direction: column;
`;

const GraphArea = styled("div")`
  position: relative;
  flex: 1;
  min-height: 0;
  max-height: 50%;
`;

const HeaderRow = styled("div")`
  z-index: 1;
  margin-top: 32px;
  margin-bottom: auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const StatsRow = styled("div")`
  display: flex;
  gap: 32px;
`;

const StatItem = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 76px;
`;

const StatusBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px 1px 5px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  width: fit-content;
`;

const PulseDot = styled("span")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--green-600);
`;

const EdgeLabel = styled("div")`
  position: absolute;
  bottom: -44px;
  transform: translateX(-50%);
  color: var(--text-secondary);
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  letter-spacing: -1%;
  border-radius: 999px;
  pointer-events: none;
  white-space: nowrap;
`;

const RADAR_SIZE = 80;
const RADAR_CX = RADAR_SIZE / 2;
const RADAR_CY = RADAR_SIZE / 2;
const RADAR_R = 39;
const SWEEP_PERIOD_MS = 4000;
const ECHO_HOLD_MS = 3000;
const ECHO_FADE_MS = 3000;
const ECHO_LIFETIME_MS = ECHO_HOLD_MS + ECHO_FADE_MS;

const RadarContainer = styled("div")`
  position: absolute;
  top: 32px;
  right: 0;
  width: ${RADAR_SIZE}px;
  height: ${RADAR_SIZE}px;
  will-change: contents;
`;

type RadarEcho = { createdAt: number; color: string };

const Radar = memo(function Radar({
  runs,
}: {
  runs: ReadonlyArray<{
    id: string;
    createdAt: number;
    ephemeral: boolean | null;
  }>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef(0);

  // Stable echo map: once a run is seen, its echo position never changes.
  // Keyed by rounded createdAt (nearest second) to survive small timestamp
  // adjustments from Zero's optimistic → server-confirmed cycle.
  const stableEchoes = useRef(new Map<number, RadarEcho>());
  const echoes = useMemo(() => {
    const now = Date.now();
    const cutoff = now - ECHO_LIFETIME_MS;

    // Expire old echoes
    for (const [key, echo] of stableEchoes.current) {
      if (echo.createdAt < cutoff) stableEchoes.current.delete(key);
    }

    for (const run of runs) {
      if (run.createdAt < cutoff) continue;
      const key = Math.round(run.createdAt / 1000);
      if (stableEchoes.current.has(key)) continue;
      stableEchoes.current.set(key, {
        createdAt: run.createdAt,
        color: run.ephemeral ? "sky" : "green",
      });
    }

    return [...stableEchoes.current.values()];
  }, [runs]);

  // Resolve CSS custom properties to actual color values canvas can use
  const resolvedColors = useRef<Map<string, string>>(new Map());
  const resolvedGray = useRef("rgba(128,128,128,0.3)");
  const resolvedBg = useRef("rgb(0,0,0)");
  useEffect(() => {
    const el = canvasRef.current?.parentElement;
    if (!el) return;
    const probe = document.createElement("div");
    el.appendChild(probe);
    probe.style.color = "var(--gray-600)";
    resolvedGray.current = getComputedStyle(probe).color;
    probe.style.color = "var(--surface-secondary)";
    resolvedBg.current = getComputedStyle(probe).color;
    // Resolve each unique step color
    const colorNames = new Set(echoes.map((e) => e.color));
    for (const name of colorNames) {
      if (!resolvedColors.current.has(name)) {
        probe.style.color = `var(--${name}-600)`;
        resolvedColors.current.set(name, getComputedStyle(probe).color);
      }
    }
    probe.remove();
  }, [echoes]);

  // Keep echoes in a ref so the animation loop reads the latest without restarting
  const echoesRef = useRef(echoes);
  echoesRef.current = echoes;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = RADAR_SIZE * dpr;
    canvas.height = RADAR_SIZE * dpr;
    ctx.scale(dpr, dpr);

    const draw = (timestamp: DOMHighResTimeStamp) => {
      const sweepAngle =
        ((timestamp % SWEEP_PERIOD_MS) / SWEEP_PERIOD_MS) * Math.PI * 2 -
        Math.PI / 2;
      const now = Date.now();

      ctx.clearRect(0, 0, RADAR_SIZE, RADAR_SIZE);

      const grayColor = resolvedGray.current;

      // Outer ring
      ctx.beginPath();
      ctx.arc(RADAR_CX, RADAR_CY, RADAR_R, 0, Math.PI * 2);
      ctx.strokeStyle = grayColor;
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Inner ring
      ctx.beginPath();
      ctx.arc(RADAR_CX, RADAR_CY, RADAR_R * 0.5, 0, Math.PI * 2);
      ctx.stroke();

      // Sweep arm with trail (drawn as layered arcs since conic gradients
      // don't support CSS color() values with appended alpha)
      const trailAngle = Math.PI * 0.4;
      const trailSteps = 8;
      for (let s = 0; s < trailSteps; s++) {
        const t = s / trailSteps;
        const a0 = sweepAngle - trailAngle * (1 - t);
        const a1 = sweepAngle - trailAngle * (1 - (s + 1) / trailSteps);
        ctx.beginPath();
        ctx.moveTo(RADAR_CX, RADAR_CY);
        ctx.arc(RADAR_CX, RADAR_CY, RADAR_R, a0, a1);
        ctx.closePath();
        ctx.fillStyle = grayColor;
        ctx.globalAlpha = t * 0.25;
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Sweep line
      ctx.beginPath();
      ctx.moveTo(RADAR_CX, RADAR_CY);
      ctx.lineTo(
        RADAR_CX + Math.cos(sweepAngle) * RADAR_R,
        RADAR_CY + Math.sin(sweepAngle) * RADAR_R
      );
      ctx.strokeStyle = grayColor;
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Echo dots
      const bgColor = resolvedBg.current;
      for (const echo of echoesRef.current) {
        const age = now - echo.createdAt;
        if (age > ECHO_LIFETIME_MS) continue;

        // Fixed angle derived from timestamp (stable per echo)
        const echoAngle =
          ((echo.createdAt % SWEEP_PERIOD_MS) / SWEEP_PERIOD_MS) * Math.PI * 2 -
          Math.PI / 2;

        // Hold at full opacity, then fade
        const opacity =
          age < ECHO_HOLD_MS
            ? 1
            : 1 - (age - ECHO_HOLD_MS) / (ECHO_LIFETIME_MS - ECHO_HOLD_MS);

        // Place dot at a semi-random radius based on timestamp
        const r = RADAR_R * (0.3 + (((echo.createdAt * 7) % 100) / 100) * 0.6);
        const dx = RADAR_CX + Math.cos(echoAngle) * r;
        const dy = RADAR_CY + Math.sin(echoAngle) * r;

        ctx.globalAlpha = opacity;

        // Border (background-colored ring)
        ctx.beginPath();
        ctx.arc(dx, dy, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = bgColor;
        ctx.fill();

        // Dot
        ctx.beginPath();
        ctx.arc(dx, dy, 3, 0, Math.PI * 2);
        ctx.fillStyle = resolvedColors.current.get(echo.color) ?? grayColor;
        ctx.fill();

        ctx.globalAlpha = 1;
      }

      // Center dot
      ctx.beginPath();
      ctx.arc(RADAR_CX, RADAR_CY, 3, 0, Math.PI * 2);
      ctx.fillStyle = grayColor;
      ctx.fill();

      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  return (
    <RadarContainer>
      <canvas
        ref={canvasRef}
        style={{ width: RADAR_SIZE, height: RADAR_SIZE }}
      />
    </RadarContainer>
  );
});

function xForBucket(bucket: number, bucketCount: number): number {
  return PADDING_X + (bucket / (bucketCount - 1)) * GRAPH_WIDTH;
}

function yForCount(count: number, maxCount: number): number {
  return PADDING_TOP + GRAPH_HEIGHT - (count / maxCount) * GRAPH_HEIGHT;
}

function areaPathFromCurve(
  curvePath: string,
  startX: number,
  endX: number
): string {
  return `${curvePath}L${endX},${BASELINE_Y}L${startX},${BASELINE_Y}Z`;
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  return `${d.getHours().toString().padStart(2, "0")}∶${d.getMinutes().toString().padStart(2, "0")}`;
}

function computeWindowMs(
  runs: ReadonlyArray<{ createdAt: number }>,
  now: number
): number {
  if (runs.length < WORKFLOW_RUNS_LIMIT) return DEFAULT_WINDOW_MS;
  // Runs are sorted createdAt desc, so the last element is the oldest.
  return now - runs[runs.length - 1]!.createdAt;
}

function computeOscilloscopeData(
  runs: ReadonlyArray<{ createdAt: number }>,
  now: number,
  windowMs: number
): { counts: number[]; maxCount: number } {
  const windowStart = now - windowMs;
  const bucketMs = windowMs / BUCKET_COUNT;
  const counts = new Array<number>(BUCKET_COUNT).fill(0);

  for (const run of runs) {
    if (run.createdAt >= windowStart && run.createdAt <= now) {
      const bucket = Math.min(
        Math.floor((run.createdAt - windowStart) / bucketMs),
        BUCKET_COUNT - 1
      );
      counts[bucket]!++;
    }
  }

  const maxCount = Math.max(...counts, 1);
  return { counts, maxCount };
}

function useAnimatedGraph(counts: readonly number[], max: number) {
  const ref = useRef({ counts: [...counts], max });
  const [current, setCurrent] = useState(ref.current);
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    if (ref.current.counts.length !== counts.length) {
      ref.current = {
        counts: new Array(counts.length).fill(0),
        max,
      };
      setCurrent(ref.current);
    }

    const tick = (timestamp: number) => {
      const dt = lastTimeRef.current ? timestamp - lastTimeRef.current : 16.67;
      lastTimeRef.current = timestamp;

      // Frame-rate independent: same visual speed at 60 Hz, 120 Hz, or anything else
      const factor = 1 - (1 - LERP_SPEED) ** (dt / 16.67);

      const prev = ref.current;
      let settled = true;

      const lerpArr = (from: number[], to: readonly number[]) =>
        from.map((v, i) => {
          const t = to[i] ?? 0;
          if (Math.abs(v - t) < 0.05) return t;
          settled = false;
          return v + (t - v) * factor;
        });

      let nextMax = max;
      if (Math.abs(prev.max - max) >= 0.05) {
        nextMax = prev.max + (max - prev.max) * factor;
        settled = false;
      }

      const next = {
        counts: lerpArr(prev.counts, counts),
        max: nextMax,
      };

      ref.current = next;
      setCurrent(next);
      if (!settled) frameRef.current = requestAnimationFrame(tick);
    };

    lastTimeRef.current = 0;
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [counts, max]);

  return current;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

type LatestRun = {
  createdAt: number;
  executions: ReadonlyArray<{
    status: "error" | "pending" | "success";
    duration: number | null;
  }>;
};

type Props = {
  runs: ReadonlyArray<{
    id: string;
    createdAt: number;
    ephemeral: boolean | null;
  }>;
  latestRun: LatestRun | null;
};

function buildPaths(
  animated: { counts: number[]; max: number },
  dataBucketX: (b: number) => number
): { curve: string; area: string } {
  const pts: Point[] = [];
  for (let i = 0; i < BUCKET_COUNT; i++) {
    pts.push({
      x: dataBucketX(i),
      y: yForCount(animated.counts[i] ?? 0, animated.max),
    });
  }
  const ext = [{ x: 0, y: pts[0]!.y }, ...pts];
  const curve = smoothPath(ext);
  const area = areaPathFromCurve(curve, ext[0]!.x, ext[ext.length - 1]!.x);
  return { curve, area };
}

export function ExecutionGraph({ runs, latestRun }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const infoTooltipRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const greenGradId = `${uid}-green`;
  const blueGradId = `${uid}-blue`;
  const fadeMaskGradId = `${uid}-fade-grad`;
  const fadeMaskId = `${uid}-fade`;
  const rightFadeGradId = `${uid}-rfade-grad`;
  const rightFadeMaskId = `${uid}-rfade`;
  const highlightClipId = `${uid}-highlight`;
  const [hoveredBucket, setHoveredBucket] = useState<number | null>(null);

  // Tick every 30s to advance the time window
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- tick triggers re-read of Date.now()
  const now = useMemo(() => Date.now(), [tick, runs]);

  const windowMs = computeWindowMs(runs, now);
  const bucketMs = windowMs / BUCKET_COUNT;

  const productionRuns = useMemo(
    () => runs.filter((r) => !r.ephemeral),
    [runs]
  );
  const soloRuns = useMemo(() => runs.filter((r) => r.ephemeral), [runs]);

  const productionData = useMemo(
    () => computeOscilloscopeData(productionRuns, now, windowMs),
    [productionRuns, now, windowMs]
  );
  const soloData = useMemo(
    () => computeOscilloscopeData(soloRuns, now, windowMs),
    [soloRuns, now, windowMs]
  );
  const sharedMax = Math.max(productionData.maxCount, soloData.maxCount, 1);

  const animated = useAnimatedGraph(productionData.counts, sharedMax);
  const animatedSolo = useAnimatedGraph(soloData.counts, sharedMax);
  const productionEmpty = productionData.counts.every((c) => c === 0);
  const soloEmpty = soloData.counts.every((c) => c === 0);
  const isEmpty = productionEmpty && soloEmpty;
  const hasSoloRuns = soloRuns.length > 0;

  const bucketSpacing = GRAPH_WIDTH / (TOTAL_DISPLAY_BUCKETS - 1);
  const dataBuckets = useMemo(
    () => Array.from({ length: BUCKET_COUNT }, (_, i) => i),
    []
  );

  const nowX = xForBucket(NOW_BUCKET, TOTAL_DISPLAY_BUCKETS);

  // Map a data bucket index (0..BUCKET_COUNT-1) to an SVG x coordinate
  const dataBucketX = useCallback(
    (bucket: number) =>
      xForBucket(
        (bucket / (BUCKET_COUNT - 1)) * NOW_BUCKET,
        TOTAL_DISPLAY_BUCKETS
      ),
    []
  );

  const paths = useMemo(
    () => buildPaths(animated, dataBucketX),
    [animated, dataBucketX]
  );
  const soloPaths = useMemo(
    () => buildPaths(animatedSolo, dataBucketX),
    [animatedSolo, dataBucketX]
  );

  const handleBucketEnter = useCallback((bucket: number) => {
    setHoveredBucket(bucket);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredBucket(null);
  }, []);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- hoveredBucket drives when we re-measure position
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    const wrapper = wrapperRef.current;
    if (!el || !wrapper) return;
    const tipRect = el.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    let offsetX = 0;
    if (tipRect.left < wrapperRect.left) {
      offsetX = wrapperRect.left - tipRect.left;
    } else if (tipRect.right > wrapperRect.right) {
      offsetX = wrapperRect.right - tipRect.right;
    }
    el.style.transform = `translateX(calc(-50% + ${offsetX}px))`;
  }, [hoveredBucket]);

  useClampRight(infoTooltipRef, wrapperRef, hoveredBucket);

  const dataRegionWidth = nowX - PADDING_X;
  const hitAreaWidth = dataRegionWidth / BUCKET_COUNT;

  // Last run stats
  const lastRun = useMemo(() => {
    if (!latestRun) return null;
    const allTerminal =
      latestRun.executions.length > 0 &&
      latestRun.executions.every(
        (e) => e.status === "success" || e.status === "error"
      );
    const succeeded =
      allTerminal && latestRun.executions.every((e) => e.status === "success");

    let duration: number | null = null;
    if (allTerminal) {
      const durations = latestRun.executions
        .filter((e) => e.duration != null)
        .map((e) => e.duration!);
      if (durations.length > 0) {
        duration = Math.max(...durations);
      }
    }

    return {
      createdAt: latestRun.createdAt,
      succeeded: allTerminal ? succeeded : null,
      duration,
    };
  }, [latestRun]);

  const windowStart = now - windowMs;

  const bucketTimeRange = useCallback(
    (bucket: number) => {
      const start = windowStart + bucket * bucketMs;
      const end = start + bucketMs;
      return `${formatTime(start)} – ${formatTime(end)}`;
    },
    [windowStart, bucketMs]
  );

  return (
    <Wrapper>
      <HeaderRow>
        <StatsRow>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Monitor
            </Text>
            <StatusBadge
              style={{
                color: "var(--text-secondary)",
                background: "var(--alpha8)",
              }}
            >
              <PulseDot />
              Live
            </StatusBadge>
          </StatItem>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Last run
            </Text>
            <Text variant="text-regular-md" color="var(--text-primary)">
              {lastRun ? formatRelativeTime(lastRun.createdAt) : "–"}
            </Text>
          </StatItem>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Duration
            </Text>
            <Text variant="text-regular-md" color="var(--text-primary)">
              {lastRun?.duration != null
                ? formatDuration(lastRun.duration)
                : "–"}
            </Text>
          </StatItem>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Status
            </Text>
            {lastRun?.succeeded != null ? (
              <StatusBadge
                style={
                  lastRun.succeeded
                    ? {
                        color: "var(--green-600)",
                        background:
                          "color-mix(in srgb, var(--green-600) 12%, transparent)",
                      }
                    : {
                        color: "var(--red-600)",
                        background:
                          "color-mix(in srgb, var(--red-600) 12%, transparent)",
                      }
                }
              >
                <Icon
                  name={lastRun.succeeded ? "check" : "x"}
                  size={10}
                  color={
                    lastRun.succeeded ? "var(--green-600)" : "var(--red-600)"
                  }
                />
                {lastRun.succeeded ? "Succeeded" : "Failed"}
              </StatusBadge>
            ) : (
              <Text variant="text-regular-md" color="var(--text-primary)">
                –
              </Text>
            )}
          </StatItem>
        </StatsRow>
      </HeaderRow>
      <Radar runs={runs} />
      <GraphArea ref={wrapperRef}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            <linearGradient id={greenGradId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--green-600)"
                stopOpacity="0.15"
              />
              <stop offset="60%" stopColor="var(--green-600)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={blueGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--sky-600)" stopOpacity="0.15" />
              <stop offset="60%" stopColor="var(--sky-600)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={fadeMaskGradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop
                offset={`${(PADDING_X / VIEW_WIDTH) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop
                offset={`${((VIEW_WIDTH - PADDING_X) / VIEW_WIDTH) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask id={fadeMaskId}>
              <rect
                x="0"
                y="0"
                width={VIEW_WIDTH}
                height={VIEW_HEIGHT}
                fill={`url(#${fadeMaskGradId})`}
              />
            </mask>
            <clipPath id={highlightClipId}>
              <rect
                x={-bucketSpacing}
                y="0"
                width={bucketSpacing * 2}
                height={VIEW_HEIGHT}
                style={{
                  transform: `translateX(${dataBucketX(hoveredBucket ?? 0)}px)`,
                  transition:
                    "transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 50ms",
                }}
              />
            </clipPath>
            <clipPath id={`${uid}-data-region`}>
              <rect x="0" y="0" width={nowX + 1} height={VIEW_HEIGHT} />
            </clipPath>
            <linearGradient
              id={rightFadeGradId}
              gradientUnits="userSpaceOnUse"
              x1={nowX}
              y1="0"
              x2={VIEW_WIDTH}
              y2="0"
            >
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="85%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask
              id={rightFadeMaskId}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={VIEW_WIDTH}
              height={VIEW_HEIGHT}
            >
              <rect
                x={nowX}
                y="0"
                width={VIEW_WIDTH - nowX}
                height={VIEW_HEIGHT}
                fill={`url(#${rightFadeGradId})`}
              />
            </mask>
          </defs>

          {/* Empty baseline (solid to now) */}
          {isEmpty && (
            <line
              x1={PADDING_X}
              y1={BASELINE_Y}
              x2={nowX}
              y2={BASELINE_Y}
              stroke="var(--gray-400)"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          )}

          <g mask={`url(#${fadeMaskId})`} clipPath={`url(#${uid}-data-region)`}>
            {/* Solo runs (blue) — drawn behind production runs */}
            {soloPaths.area && !soloEmpty && (
              <path d={soloPaths.area} fill={`url(#${blueGradId})`} />
            )}
            {soloPaths.curve && !soloEmpty && (
              <path
                d={soloPaths.curve}
                fill="none"
                stroke={
                  hoveredBucket !== null ? "var(--sky-400)" : "var(--sky-600)"
                }
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                style={{ transition: "stroke 150ms ease" }}
              />
            )}
            {soloPaths.curve && hoveredBucket !== null && !soloEmpty && (
              <path
                d={soloPaths.curve}
                fill="none"
                stroke="var(--sky-600)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                clipPath={`url(#${highlightClipId})`}
              />
            )}

            {/* Production runs (green) */}
            {paths.area && !productionEmpty && (
              <path d={paths.area} fill={`url(#${greenGradId})`} />
            )}
            {paths.curve && !productionEmpty && (
              <path
                d={paths.curve}
                fill="none"
                stroke={
                  hoveredBucket !== null
                    ? "var(--green-400)"
                    : "var(--green-600)"
                }
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                style={{ transition: "stroke 150ms ease" }}
              />
            )}
            {paths.curve && hoveredBucket !== null && !productionEmpty && (
              <path
                d={paths.curve}
                fill="none"
                stroke="var(--green-600)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                clipPath={`url(#${highlightClipId})`}
              />
            )}
          </g>

          {/* Dashed future baseline (now to right edge, fades at right) */}
          <g mask={`url(#${rightFadeMaskId})`}>
            <line
              x1={nowX}
              y1={BASELINE_Y}
              x2={VIEW_WIDTH}
              y2={BASELINE_Y}
              stroke="var(--gray-400)"
              strokeWidth="2"
              strokeDasharray="6 4"
              vectorEffect="non-scaling-stroke"
            />
          </g>

          {/* Invisible hover hit areas (data region only) */}
          {dataBuckets.map((h) => (
            <rect
              key={h}
              x={PADDING_X + h * hitAreaWidth}
              y={0}
              width={hitAreaWidth}
              height={VIEW_HEIGHT}
              fill="transparent"
              onMouseEnter={() => handleBucketEnter(h)}
            />
          ))}
        </svg>
        <EdgeLabel
          style={{
            left: `${(dataBucketX(0) / VIEW_WIDTH) * 100}%`,
          }}
        >
          {formatTime(windowStart)}
        </EdgeLabel>
        <ChartMarker
          style={{
            left: `${(nowX / VIEW_WIDTH) * 100}%`,
          }}
        />
        <ChartPill
          style={{
            left: `${(nowX / VIEW_WIDTH) * 100}%`,
            color: "var(--text-secondary)",
            background: "var(--gray-400)",
          }}
        >
          Now
        </ChartPill>
        {hoveredBucket !== null && (
          <>
            <ChartMarker
              style={{
                left: `${(dataBucketX(hoveredBucket) / VIEW_WIDTH) * 100}%`,
                background: "var(--text-primary)",
                transition: "left 150ms ease",
              }}
            />
            <ChartPill
              ref={tooltipRef}
              style={{
                left: `${(dataBucketX(hoveredBucket) / VIEW_WIDTH) * 100}%`,
                background: "var(--text-primary)",
                color: "var(--text-invert)",
                transition: "left 150ms ease",
              }}
            >
              {formatTime(windowStart + hoveredBucket * bucketMs)}
            </ChartPill>
            <ChartTooltip
              ref={infoTooltipRef}
              style={{
                left: `calc(${(dataBucketX(hoveredBucket) / VIEW_WIDTH) * 100}% + 8px)`,
                transition: "left 150ms ease",
              }}
            >
              <ChartTooltipHeader>
                {bucketTimeRange(hoveredBucket)}
              </ChartTooltipHeader>
              <ChartTooltipRow>
                <ChartTooltipDot style={{ background: "var(--green-600)" }} />
                <span>Runs</span>
                <ChartTooltipCount>
                  {productionData.counts[hoveredBucket] ?? 0}
                </ChartTooltipCount>
              </ChartTooltipRow>
              {hasSoloRuns && (
                <ChartTooltipRow>
                  <ChartTooltipDot style={{ background: "var(--sky-600)" }} />
                  <span>Solo runs</span>
                  <ChartTooltipCount>
                    {soloData.counts[hoveredBucket] ?? 0}
                  </ChartTooltipCount>
                </ChartTooltipRow>
              )}
            </ChartTooltip>
          </>
        )}
      </GraphArea>
    </Wrapper>
  );
}
